/**
 * Utility functions for formatting and sanitizing text/HTML content.
 * These functions can be reused across the application.
 */

/**
 * Escapes HTML special characters to prevent XSS
 */
export const escapeHtml = (text: string): string => {
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
};

/**
 * Formats summaryData object into HTML/text format where each key becomes a title
 * and each value becomes text below it with line breaks.
 *
 * - Strings are attempted to be parsed as JSON first.
 * - Object keys become `<h3>` headings.
 * - Array values become `<ul>` bullet lists.
 * - Other values become `<p>` paragraphs.
 */
export const formatSummaryData = (summaryData: unknown): string => {
  try {
    // If summaryData is a string, try to parse it as JSON
    let dataObj: Record<string, unknown>;
    if (typeof summaryData === "string") {
      try {
        dataObj = JSON.parse(summaryData);
      } catch {
        // If it's not valid JSON, return as is
        return summaryData;
      }
    } else if (typeof summaryData === "object" && summaryData !== null) {
      dataObj = summaryData as Record<string, unknown>;
    } else {
      return String(summaryData);
    }

    // Format each key-value pair
    const formattedParts: string[] = [];

    for (const [key, value] of Object.entries(dataObj)) {
      // Add title (key) as heading
      formattedParts.push(`<h3>${escapeHtml(String(key))}</h3>`);

      // Check if value is an array and render as bullet list
      if (Array.isArray(value)) {
        const listItems = value.map((item) => `<li>${escapeHtml(String(item))}</li>`).join("");
        formattedParts.push(`<ul>${listItems}</ul>`);
      } else {
        // Add value as paragraph
        const valueStr = typeof value === "object" ? JSON.stringify(value, null, 2) : String(value);
        formattedParts.push(`<p>${escapeHtml(valueStr)}</p>`);
      }

      // Add line break between sections
      formattedParts.push("<br>");
    }

    return formattedParts.join("");
  } catch (error) {
    console.error("[formatSummaryData] Error formatting summaryData:", error);
    return String(summaryData);
  }
};
